import React, { createContext, useContext, useState, ReactNode, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box
} from '@mui/material';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';

interface ConfirmDialogOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  severity?: 'warning' | 'error' | 'info';
}

interface ConfirmDialogContextType {
  showConfirm: (options: ConfirmDialogOptions) => Promise<boolean>;
}

const ConfirmDialogContext = createContext<ConfirmDialogContextType | undefined>(undefined);

interface ConfirmDialogProviderProps {
  children: ReactNode;
}

export const ConfirmDialogProvider: React.FC<ConfirmDialogProviderProps> = ({ children }) => {
  const { isDark } = useTheme();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmDialogOptions>({
    title: 'Confirmation',
    message: '',
    confirmText: 'Confirmer',
    cancelText: 'Annuler',
    severity: 'warning'
  });
  const [resolvePromise, setResolvePromise] = useState<((value: boolean) => void) | null>(null);

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDark ? 'dark' : 'light',
        },
        components: {
          MuiDialog: {
            styleOverrides: {
              paper: {
                backgroundImage: 'none',
                backgroundColor: isDark ? '#1e293b' : '#ffffff', // slate-800 for dark mode
              },
            },
          },
        },
      }),
    [isDark]
  );

  const showConfirm = (dialogOptions: ConfirmDialogOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setOptions({
        title: dialogOptions.title || 'Confirmation',
        message: dialogOptions.message,
        confirmText: dialogOptions.confirmText || 'Confirmer',
        cancelText: dialogOptions.cancelText || 'Annuler',
        severity: dialogOptions.severity || 'warning'
      });
      setResolvePromise(() => resolve);
      setOpen(true);
    });
  };

  const handleConfirm = () => {
    if (resolvePromise) {
      resolvePromise(true);
    }
    handleClose();
  };

  const handleCancel = () => {
    if (resolvePromise) {
      resolvePromise(false);
    }
    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
    setResolvePromise(null);
  };

  const getSeverityColor = () => {
    switch (options.severity) {
      case 'error':
        return '#f44336';
      case 'warning':
        return '#ff9800';
      case 'info':
        return '#2196f3';
      default:
        return '#ff9800';
    }
  };

  const contextValue: ConfirmDialogContextType = {
    showConfirm,
  };

  return (
    <ConfirmDialogContext.Provider value={contextValue}>
      {children}
      
      <MuiThemeProvider theme={muiTheme}>
        <Dialog
          open={open}
          onClose={handleCancel}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: `${getSeverityColor()}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <WarningIcon sx={{ color: getSeverityColor(), fontSize: 20 }} />
              </Box>
              <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                {options.title}
              </Typography>
            </Box>
          </DialogTitle>
          
          <DialogContent sx={{ pt: 1, pb: 2 }}>
            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
              {options.message}
            </Typography>
          </DialogContent>
          
          <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
            <Button
              onClick={handleCancel}
              variant="outlined"
              sx={{
                minWidth: 100,
                borderColor: isDark ? 'rgba(255, 255, 255, 0.23)' : 'grey.300',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.5)' : 'grey.400',
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'grey.50',
                }
              }}
            >
              {options.cancelText}
            </Button>
            <Button
              onClick={handleConfirm}
              variant="contained"
              sx={{
                minWidth: 100,
                backgroundColor: getSeverityColor(),
                '&:hover': {
                  backgroundColor: getSeverityColor(),
                  filter: 'brightness(0.9)',
                }
              }}
            >
              {options.confirmText}
            </Button>
          </DialogActions>
        </Dialog>
      </MuiThemeProvider>
    </ConfirmDialogContext.Provider>
  );
};

export const useConfirm = (): ConfirmDialogContextType => {
  const context = useContext(ConfirmDialogContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmDialogProvider');
  }
  return context;
};
